import {
    Button,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { Link as ReactRouterLink } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { DeleteButton, EditButton } from '@frontend/components/elements/common';
import { ConfirmationModal } from '@frontend/components/elements/modals/ConfirmationModal';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';

interface IDeviceAccessoryTypeWithAuthorization {
    authorization: {
        canDelete: boolean;
    };
    deviceType: IDeviceType;
    id: string;
    name: string;
}

export interface IDeviceType {
    id: string;
    manufacturer: string;
    model: string;
    name: string;
    version: string;
}

export function DeviceAccessoryTypesList() {
    const [showDeleteModel, setShowDeleteModal] = useState<boolean>(false);
    const [deviceAccessoryTypeIdToDelete, setDeviceAccessoryTypeToDelete] = useState<string | undefined>(undefined);
    const [deviceAccessoryTypes, setDeviceAccessoryTypes] = useState<IDeviceAccessoryTypeWithAuthorization[]>([]);

    assertHasAuthorization(ManagerAuthorization.deviceAndAccessoryTypes);

    useEffect(() => {
        loadDeviceAccessoryTypes();
    }, []);

    return (
        <PageTemplate>
            <PageHeader
                action={
                    <Button
                        as={ReactRouterLink}
                        iconLeft="add"
                        to="/deviceAccessoryTypes/add"
                    >
                        {translateKey('deviceAccessoryTypes.add')}
                    </Button>
                }
                title={translateKey('deviceAccessoryTypes.deviceAccessoryTypes')}
            />
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('common.name')}</TableHeader>
                            <TableHeader>{translateKey('devices.type')}</TableHeader>
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {deviceAccessoryTypes.map((deviceAccessoryType) => (
                            <TableRow key={deviceAccessoryType.id}>
                                <TableCell>{deviceAccessoryType.name}</TableCell>
                                <TableCell>{deviceAccessoryType.deviceType?.name ?? ''}</TableCell>
                                <TableCell align="right">
                                    <EditButton to={`/deviceAccessoryTypes/${deviceAccessoryType.id}`} />
                                    {deviceAccessoryType.authorization.canDelete && (
                                        <DeleteButton onClick={() => openDeleteModal(deviceAccessoryType.id)} />
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <ConfirmationModal
                handleNo={async () => await closeDeleteModal()}
                handleYes={async () => await deleteDeviceAccessoryType()}
                isOpen={showDeleteModel}
                question={translateKey('deviceAccessoryTypes.sureToDelete')}
                title={translateKey('common.notice')}
            />
        </PageTemplate>
    );

    async function loadDeviceAccessoryTypes() {
        const url = '/api/deviceAccessoryTypes?includeItemAuthorizations=canDelete';
        const result = await fetchJson(url, 'GET');
        setDeviceAccessoryTypes(result.json.items);
    }

    function openDeleteModal(deviceAccessoryTypeId: string) {
        setDeviceAccessoryTypeToDelete(deviceAccessoryTypeId);
        setShowDeleteModal(true);
    }

    function closeDeleteModal() {
        setDeviceAccessoryTypeToDelete(undefined);
        setShowDeleteModal(false);
    }

    async function deleteDeviceAccessoryType() {
        if (deviceAccessoryTypeIdToDelete) {
            await fetchJson(`/api/deviceAccessoryTypes/${deviceAccessoryTypeIdToDelete}`, 'DELETE');
        }
        closeDeleteModal();
        await loadDeviceAccessoryTypes();
    }
}
